import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, Renderer2, AfterViewInit, ViewChild, ElementRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Subscription, forkJoin, of, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { Router } from '@angular/router';
import { environment } from '../../../../../my-app/src/environments/environment';

// Import Angular Material modules
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { WidgetActionService } from '../../shared-services/widget-action.service';

@Component({
  selector: 'app-entity-management',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatSelectModule,
    MatFormFieldModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './entity-management.component.html',
  styleUrls: ['./entity-management.component.scss']
})
export class EntityManagementComponent implements OnInit, OnDestroy {
  // sync header/body horizontal scroll
  @ViewChild('headerScroll', { static: false }) headerScroll!: ElementRef;
  @ViewChild('bodyScroll', { static: false }) bodyScroll!: ElementRef;
  private isSyncingScroll = false;
  // header drag state
  private isHeaderDragging = false;
  private headerDragStartX = 0;
  private headerDragStartScrollLeft = 0;
  private headerMouseMoveListener?: () => void;
  private headerMouseUpListener?: () => void;

  @Input() data: any;
  @Output() recordSelected = new EventEmitter<any>();
  @Output() editRecord = new EventEmitter<any>();

  records: any[] = [];
  selectedRecord: any = null;
  loading = true;
  currentFilters: any = {};
  pageIndex: number = 0;
  pageSize: number = 100;
  selection = new Set<any>();
  allSelected = false;
  showEditColumn: boolean = false;
  headerTitle: string = '';
  sortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';

  // Column resizing properties
  columnWidths: (number | undefined)[] = [];
  private resizingColumnIndex: number = -1;
  private startX: number = 0;
  private startWidth: number = 0;
  private mouseMoveListener!: () => void;
  private mouseUpListener!: () => void;

  private filterSub?: Subscription;
  private refreshSub?: Subscription;
  private widgetActionSub?: Subscription;
  private dropdownKeys: string[] = [];

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private globalFilterService: GlobalFilterService,
    private renderer: Renderer2,
    private widgetActionService: WidgetActionService,
    private router: Router
  ) { }

  ngOnInit(): void {
    if (this.data?.header?.title) {
      this.headerTitle = this.data.header.title;
    }

    if (this.data?.header?.dropdowns) {
      this.dropdownKeys = this.data.header.dropdowns.map((d: any) => d.key);
      this.data.header.dropdowns.forEach((dropdown: any) => {
        if (dropdown.defaultValue) {
          this.currentFilters[dropdown.key] = dropdown.defaultValue;
        }
      });
    }

    const initObservables: { [key: string]: Observable<any> } = {
      mainData: this.loadRecords()
    };

    // Initialize pagination from config if provided
    if (this.data?.pagination?.enabled && this.data.pagination.pageSize) {
      this.pageSize = this.data.pagination.pageSize;
    }

    if (this.data?.header?.dropdowns) {
      this.data.header.dropdowns.forEach((dropdown: any, index: number) => {
        if (dropdown.options?.apiUrl) {
          initObservables[`dropdown${index}`] = this.fetchDropdownOptions(dropdown);
        }
      });
    }

    forkJoin(initObservables).subscribe({
      error: (err) => {
        console.error("Initialization failed:", err);
        this.loading = false;
      }
    });

    this.setupFilterListener();
    this.setupRefreshListener();


    this.widgetActionSub = this.widgetActionService.action$.subscribe((event: any) => {
      console.log('Received widget action event in SideEntityFormComponent:', event);
      console.log('Event received:', event);
      console.log('Form widget data:', this.data);
      console.log('actionListener:', this.data?.actionListener);
      // console.log('sourceWidgetId:', event.sourceWidgetId);

      // 1️⃣ Check JSON-driven listener rule
      if (
        !this.data?.actionListener ||
        !this.data.actionListener.includes(event.sourceWidgetId)
      ) {
        console.log('Guard blocked execution');
        return;
      }
      console.log('Guard passed 2');

      if (event.actionId == 'refresh') {
        this.loadRecords().subscribe();
      }

      // 2️⃣ React to navbar actionId
      // switch (event.actionId) {

      //   case 'add':
      //      console.log('➡️ Calling openNewForm()');
      //     this.openNewForm();
      //     break;

      //   case 'refresh':
      //     this.reloadForm();
      //     break;

      //   case 'delete':
      //     this.prepareDelete();
      //     break;
      // }
    });

  }

  ngAfterViewInit(): void {
    // No-op here; we use event bindings in template to call handlers.
  }

  onHeaderMouseDown(evt: MouseEvent): void {
    // Start dragging the header to pan horizontally
    evt.preventDefault();
    this.isHeaderDragging = true;
    this.headerDragStartX = evt.clientX;
    if (this.bodyScroll && this.bodyScroll.nativeElement) {
      this.headerDragStartScrollLeft = this.bodyScroll.nativeElement.scrollLeft || 0;
    }

    // add document listeners
    this.headerMouseMoveListener = this.renderer.listen('document', 'mousemove', (e: MouseEvent) => this.onHeaderMouseMove(e));
    this.headerMouseUpListener = this.renderer.listen('document', 'mouseup', () => this.onHeaderMouseUp());

    // add dragging class for cursor change
    try { this.renderer.addClass(this.headerScroll.nativeElement, 'dragging'); } catch (e) { }
  }

  onHeaderMouseMove(evt: MouseEvent): void {
    if (!this.isHeaderDragging || !this.bodyScroll || !this.bodyScroll.nativeElement) return;
    const delta = evt.clientX - this.headerDragStartX;
    const newScrollLeft = this.headerDragStartScrollLeft - delta;
    this.bodyScroll.nativeElement.scrollLeft = newScrollLeft;
    // sync header to body
    try { this.headerScroll.nativeElement.scrollLeft = newScrollLeft; } catch (e) { }
  }

  onHeaderMouseUp(): void {
    this.isHeaderDragging = false;
    if (this.headerMouseMoveListener) { this.headerMouseMoveListener(); this.headerMouseMoveListener = undefined; }
    if (this.headerMouseUpListener) { this.headerMouseUpListener(); this.headerMouseUpListener = undefined; }
    try { this.renderer.removeClass(this.headerScroll.nativeElement, 'dragging'); } catch (e) { }
  }

  fetchDropdownOptions(dropdown: any): Observable<any> {
    const { apiUrl, key, payload } = dropdown.options;
    if (payload) {
      return this.configService.getDropdownOptions(apiUrl, payload).pipe(
        map((res: any) => {
          const optionsArray = key ? res[key] : res;
          dropdown.options = Array.isArray(optionsArray) ? optionsArray : [];
          return dropdown;
        })
      );
    } else {
      return this.http.get(apiUrl).pipe(map((res: any) => {
        const optionsArray = key ? res[key] : res;
        dropdown.options = Array.isArray(optionsArray) ? optionsArray : [];
        return dropdown;
      }));
    }
  }

  setupFilterListener(): void {
    this.filterSub = this.globalFilterService.filter$.subscribe((filters) => {
      if (!filters || !filters.widgetId || filters.widgetId === this.data.widgetId) {
        return; // Ignore updates from itself or those without a widgetId
      }
      const newFilters = filters.requestParams || filters.queryParams || {};
      this.currentFilters = { ...this.currentFilters, ...newFilters };
      this.loadRecords().subscribe();
    });
  }

  setupRefreshListener(): void {
    this.refreshSub = this.globalFilterService.widgetRefresh$.subscribe((widgetId) => {
      if (widgetId === this.data.widgetId) {
        this.loadRecords().subscribe();
      }
    });
  }

  ngOnDestroy(): void {
    this.filterSub?.unsubscribe();
    this.refreshSub?.unsubscribe();
    this.stopResizing(); // Clean up listeners if component is destroyed during resize
    this.widgetActionSub?.unsubscribe();

  }

  onBodyScroll(event: any): void {
    if (!this.headerScroll || this.isSyncingScroll) return;
    try {
      this.isSyncingScroll = true;
      const scrollLeft = event.target.scrollLeft;
      this.headerScroll.nativeElement.scrollLeft = scrollLeft;
    } finally {
      // allow other scrolls after a tiny delay to avoid recursion
      setTimeout(() => (this.isSyncingScroll = false), 0);
    }
  }

  onHeaderScroll(event: any): void {
    if (!this.bodyScroll || this.isSyncingScroll) return;
    try {
      this.isSyncingScroll = true;
      const scrollLeft = event.target.scrollLeft;
      this.bodyScroll.nativeElement.scrollLeft = scrollLeft;
    } finally {
      setTimeout(() => (this.isSyncingScroll = false), 0);
    }
  }



  loadRecords(): Observable<any[]> {
    this.loading = true;
    if (this.data?.widgetId) {
      this.globalFilterService.updateFilterSelection('loading', [this.loading], false, this.data.widgetId);
    }
    const selectedRecordId = this.selectedRecord ? this.selectedRecord.id : null;

    if (Array.isArray(this.data)) {
      this.records = this.data;
      // If data is an array, don't try to access object properties
      this.showEditColumn = false;
      if (this.records.length > 0) {
        this.selectRecord(this.records[0]);
      }
      this.loading = false;
      return of(this.records);
    }

    // if (!this.data?.apiUrl) {
    //   this.records = [];
    //   this.loading = false;
    //   if (this.data?.widgetId) {
    //     this.globalFilterService.updateFilterSelection('loading', [this.loading], false, this.data.widgetId);
    //   }
    //   return of([]);
    // }

    // ✅ If apiUrl is empty, fallback to jsonData
    if (!this.data?.apiUrl) {
      const jsonPath = this.data?.jsonData;

      if (!jsonPath) {
        this.records = [];
        this.loading = false;
        if (this.data?.widgetId) {
          this.globalFilterService.updateFilterSelection('loading', [this.loading], false, this.data.widgetId);
        }
        return of([]);
      }

      // If jsonData is a relative path, it must be served from /assets or /public root.
      // Best: put file inside src/assets and set jsonData: "assets/part_entity_dummy_100_rows.json"
      return this.http.get<any[]>(jsonPath).pipe(
        map((res: any) => {
          // If JSON is an array => use it directly
          const result = Array.isArray(res) ? res : (this.data?.key ? res?.[this.data.key] : []);
          this.records = Array.isArray(result) ? result : [];

          // Determine whether to show edit column based on JSON content or widget config
          this.showEditColumn = !!(this.data?.showEdit || this.data?.quotes?.showEdit || res?.showEdit);

          // Auto-generate columns if not provided
          if (!this.data.columns && this.records.length > 0) {
            const firstRecord = this.records[0];
            const newColumns = Object.keys(firstRecord)
              .filter(k => k !== 'id')
              .map(k => ({
                key: k,
                label: k.charAt(0).toUpperCase() + k.slice(1),
                sortable: true
              }));

            this.data.columns = newColumns;

            // Initialize columnWidths
            this.columnWidths = [40];
            newColumns.forEach(() => this.columnWidths.push(150));
          }

          // Auto-select first record
          if (this.records.length > 0) {
            this.selectRecord(this.records[0]);
          } else {
            this.selectRecord(null);
          }

          this.loading = false;
          if (this.data?.widgetId) {
            this.globalFilterService.updateFilterSelection('loading', [this.loading], false, this.data.widgetId);
          }

          return this.records;
        }),
        catchError((error) => {
          console.error('Error loading JSON fallback:', error);
          this.records = [];
          this.loading = false;
          if (this.data?.widgetId) {
            this.globalFilterService.updateFilterSelection('loading', [this.loading], false, this.data.widgetId);
          }
          return of([]);
        })
      );
    }


    const method = this.data?.method?.toLowerCase() || 'post';
    // Build request filters respecting currentFilters, any defaultFilters, and pagination
    const baseRequestParams: any = {
      ...(this.data?.defaultFilters || {}),
      ...(this.currentFilters || {})
    };

    // Add pagination params if pagination enabled
    if (this.data?.pagination?.enabled) {
      baseRequestParams.limit = this.pageSize;
      baseRequestParams.offset = this.pageIndex * this.pageSize;
      // baseRequestParams.is_active = true;
    }

    if (this.data?.pagination?.isactive) {
      if (this.data?.pagination.limit) {
        baseRequestParams.limit = this.data?.pagination.limit;
      }
      baseRequestParams.is_active = true;
    }

    let filtersToSend: any = this.data?.payload || { requestParams: baseRequestParams };

    // If payload exists but doesn't contain requestParams, attach built request params
    if (this.data?.payload && !this.data.payload.requestParams) {
      filtersToSend = { ...this.data.payload, requestParams: baseRequestParams };
    }

    // Ensure Part API receives pagination if not provided in JSON config
    const partApiIdentifier = '/spriced/platform/part/Part';
    const computedLimit = this.pageSize;
    const computedOffset = this.pageIndex * this.pageSize;

    if (this.data?.apiUrl && this.data.apiUrl.includes(partApiIdentifier)) {
      // Normalize where paging/filters live (top-level or inside requestParams)
      const paramsRef: any = filtersToSend.requestParams ? filtersToSend.requestParams : filtersToSend;

      if (paramsRef.limit == null) paramsRef.limit = computedLimit;
      if (paramsRef.offset == null) paramsRef.offset = computedOffset;

      // Ensure API receives is_active as expected
      if (paramsRef.is_active == null) paramsRef.is_active = ['true'];

      // Remove UI-specific keys that the Part API doesn't expect
      delete paramsRef.modle; // misspelled 'model' coming from dropdown
      delete paramsRef.model; // in case correct spelling used elsewhere
      delete paramsRef.entity;

      // Write back normalized params
      if (filtersToSend.requestParams) {
        filtersToSend.requestParams = paramsRef;
      } else {
        filtersToSend = paramsRef;
      }
    }

    return this.configService.getApi(this.data.apiUrl, filtersToSend, {
      skipDefaultPayload: true,
      useQueryParam: method === 'get'
    }, method).pipe(
      map((res: any) => {
        const key = this.data?.key || null;
        const result = key ? res?.[key] : res;
        this.records = Array.isArray(result) ? result : [];

        // Respect showEdit flag coming from API response or widget config
        this.showEditColumn = !!(this.data?.showEdit || this.data?.quotes?.showEdit || res?.showEdit);

        if (!this.data.columns && this.records.length > 0) {
          const firstRecord = this.records[0];
          const newColumns = Object.keys(firstRecord)
            .filter(key => key !== 'id')
            .map(key => ({
              key: key,
              label: key.charAt(0).toUpperCase() + key.slice(1),
              sortable: true
            }));
          this.data.columns = newColumns;

          // Initialize columnWidths with default values
          this.columnWidths = [40]; // Checkbox column
          newColumns.forEach(() => this.columnWidths.push(150)); // Default width for data columns
        }

        if (this.records.length > 0) {
          let recordToSelect = this.records[0]; // Default to first record
          if (selectedRecordId) {
            const reselectedRecord = this.records.find(r => r.id === selectedRecordId);
            if (reselectedRecord) {
              recordToSelect = reselectedRecord;
            }
          }
          this.selectRecord(recordToSelect);
        } else {
          this.selectRecord(null); // Clear selection if no records
        }

        this.loading = false;
        if (this.data?.widgetId) {
          this.globalFilterService.updateFilterSelection('loading', [this.loading], false, this.data.widgetId);
        }
        return this.records;
      }),
      catchError((error) => {
        console.error('Error loading records:', error);
        this.loading = false;
        this.records = [];
        if (this.data?.widgetId) {
          this.globalFilterService.updateFilterSelection('loading', [this.loading], false, this.data.widgetId);
        }
        return of([]); // Return an empty observable to complete the stream
      })
    );
  }

  onResizeStart(event: MouseEvent, index: number): void {
    event.preventDefault();
    this.resizingColumnIndex = index;
    this.startX = event.clientX;
    const th = (event.target as HTMLElement).parentElement;
    this.startWidth = th?.offsetWidth || 0;

    this.mouseMoveListener = this.renderer.listen('document', 'mousemove', this.onResizing.bind(this));
    this.mouseUpListener = this.renderer.listen('document', 'mouseup', this.stopResizing.bind(this));
  }

  onResizing(event: MouseEvent): void {
    if (this.resizingColumnIndex > -1) {
      const deltaX = event.clientX - this.startX;
      const newWidth = this.startWidth + deltaX;
      if (newWidth > 50) { // Minimum column width
        this.columnWidths[this.resizingColumnIndex] = newWidth;
      }
    }
  }

  stopResizing(): void {
    this.resizingColumnIndex = -1;
    if (this.mouseMoveListener) {
      this.mouseMoveListener();
    }
    if (this.mouseUpListener) {
      this.mouseUpListener();
    }
  }

  sortBy(columnKey: string) {
    if (this.sortColumn === columnKey) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = columnKey;
      this.sortDirection = 'asc';
    }

    this.records.sort((a, b) => {
      const valA = a[columnKey];
      const valB = b[columnKey];

      if (valA < valB) {
        return this.sortDirection === 'asc' ? -1 : 1;
      }
      if (valA > valB) {
        return this.sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }

  getSortIcon(columnKey: string): string {
    if (this.sortColumn !== columnKey) {
      return 'unfold_more';
    }
    if (this.sortDirection === 'asc') {
      return 'arrow_upward';
    }
    return 'arrow_downward';
  }

  handleHeaderAction(actionId: string) {
    console.log('Header action clicked:', actionId);
    if (actionId === 'refresh') {
      this.loadRecords().subscribe();
    }
  }

  handleTabAction(actionId: string) {
    console.log('Tab action clicked:', actionId);
    // Update active state
    if (this.data?.header?.components) {
      this.data.header.components.forEach((comp: any) => {
        comp.isActive = comp.actionId === actionId;
      });
    }
    // Find the component and set the apiUrl
    const component = this.data?.header?.components?.find((comp: any) => comp.actionId === actionId);
    if (component?.apiUrl) {
      this.data.apiUrl = component.apiUrl;
      this.loadRecords().subscribe();
    }
  }

  toggleAllSelection() {
    this.allSelected = !this.allSelected;
    if (this.allSelected) {
      this.records.forEach(record => this.selection.add(record));
    } else {
      this.selection.clear();
    }
  }

  toggleSelection(record: any) {
    if (this.selection.has(record)) {
      this.selection.delete(record);
    } else {
      this.selection.add(record);
    }
    this.allSelected = this.selection.size === this.records.length;
  }

  selectRecord(record: any) {
    this.selectedRecord = record;
    this.recordSelected.emit(record);
    if (this.data?.widgetId) {
      // Pass an array with the record, or an empty array if the record is null
      this.globalFilterService.updateFilterSelection(this.data.widgetId, record ? [record] : [], false, this.data.widgetId);
    }
  }

  nextPage(): void {
    this.pageIndex += 1;
    this.loadRecords().subscribe();
  }

  prevPage(): void {
    if (this.pageIndex > 0) {
      this.pageIndex -= 1;
      this.loadRecords().subscribe();
    }
  }

  onDropdownSelectionChange(dropdownKey: string, selectedValue: any): void {
    if (dropdownKey === 'entity') {
      this.headerTitle = selectedValue;
      if (selectedValue === 'dn_pricing') {
        this.data.apiUrl = `${environment.API_BASE_URL}/spriced/platform/dn_pricing/DN_Pricing_Read`;
      } else if (selectedValue === 'part') {
        this.data.apiUrl = `${environment.API_BASE_URL}/spriced/platform/part/Part`;
      } else if (selectedValue === 'base_price') {
        this.data.apiUrl = `${environment.API_BASE_URL}/spriced/platform/base_price/Base_Price`;
      } else if (selectedValue === 'engine_model') {
        this.data.apiUrl = `${environment.API_BASE_URL}/spriced/platform/engine_model/EngineModelDropDown`;
      } else if (selectedValue === 'p_pricing_action') {
        this.data.apiUrl = `${environment.API_BASE_URL}/spriced/platform/p_pricing_action/pricingActionDropdown`;
      } else if (selectedValue === 'xchange_reason') {
        this.data.apiUrl = `${environment.API_BASE_URL}/spriced/platform/xchange_reason/xchangeReasonDropdown`;
      } else if (selectedValue === 'ecc') {
        this.data.apiUrl = `${environment.API_BASE_URL}/spriced/platform/ecc/eccdropdown`;
      } else {
        // optional fallback (keep part as default)
        this.data.apiUrl = `${environment.API_BASE_URL}/spriced/platform/part/Part`;
      }
    }

    this.currentFilters[dropdownKey] = selectedValue;
    this.data.columns = undefined; // Clear existing columns to force regeneration
    this.loadRecords().subscribe();

    if (this.data?.widgetId) {
      const isQueryParam = this.data?.method?.toLowerCase() === 'get';
      this.globalFilterService.updateFilterSelection(dropdownKey, [selectedValue], isQueryParam, this.data.widgetId);
    }
  }

  openEdit(record: any, event?: Event) {
    if (event && typeof (event as any).stopPropagation === 'function') {
      (event as any).stopPropagation();
    }
    this.editRecord.emit({ record, config: this.data });
  }
}